/**
 * Data processing service for transforming workout data into various formats
 * Centralizes all data transformation logic previously scattered in WorkoutService
 */

import type {
  Workout,
  WorkoutExercise,
  WeeklyStats,
  ExtendedCalendarDay,
  CategorizedOneRepMax,
  MuscleGroup
} from '../types'
import { calculate1RM } from '../lib/analysis'
import { exerciseProcessor } from './exerciseProcessor'
import { getLocalDayKey } from '../lib/timezone-utils'

export class DataProcessor {
  /**
   * Ensure all exercises in workouts are properly processed with muscle group data
   */
  static ensureWorkoutsProcessed(workouts: Workout[]): Workout[] {
    return workouts.map((workout) => ({
      ...workout,
      exercises: workout.exercises?.map((exercise) => {
        // Skip if already processed
        if (exercise.primaryMover) {
          return exercise
        }

        // Process exercise and add muscle group data
        const processedExercise = exerciseProcessor.processExercise({
          templateId: exercise.exercise_template_id,
          name: exercise.title
        })

        return {
          ...exercise,
          primaryMover: processedExercise.primaryMover,
          secondaryMovers: processedExercise.secondaryMovers,
          isBodyweight: processedExercise.isBodyweight,
          isAssisted: processedExercise.isAssisted,
          detectionMethod: processedExercise.detectionMethod
        }
      })
    }))
  }

  /**
   * Get workout color using consistent hash-based algorithm
   */
  static getWorkoutColor(title: string): string {
    const colors = [
      '#ef4444', // red
      '#f97316', // orange
      '#eab308', // yellow
      '#22c55e', // green
      '#3b82f6', // blue
      '#8b5cf6', // purple
      '#ec4899' // pink
    ]

    // Simple hash function for consistent colors
    let hash = 0
    for (let i = 0; i < title.length; i++) {
      hash = title.charCodeAt(i) + ((hash << 5) - hash)
    }

    return colors[Math.abs(hash) % colors.length]
  }

  /**
   * Get muscle groups from processed exercise data
   */
  static getMuscleGroupsFromExercise(
    exercise: WorkoutExercise,
    includeSecondaryMovers = false
  ): MuscleGroup[] {
    const muscleGroups: MuscleGroup[] = []

    // Add primary mover
    if (exercise.primaryMover) {
      muscleGroups.push(exercise.primaryMover)
    }

    // Add secondary movers if enabled
    if (includeSecondaryMovers && exercise.secondaryMovers) {
      muscleGroups.push(...exercise.secondaryMovers)
    }

    return muscleGroups
  }

  /**
   * Transform workouts into calendar data with proper date aggregation
   * Handles multiple workouts per day and timezone conversion
   */
  static processWorkoutsToCalendar(workouts: Workout[]): ExtendedCalendarDay[] {
    // Ensure all workouts are processed
    const processedWorkouts = this.ensureWorkoutsProcessed(workouts)

    // Add workout colors
    const workoutsWithColors = processedWorkouts.map((workout) => ({
      ...workout,
      color: this.getWorkoutColor(workout.title || '')
    }))

    // Aggregate workouts by local date using Map for O(n) performance
    const calendarMap = new Map<string, ExtendedCalendarDay>()

    workoutsWithColors.forEach((workout) => {
      // Browser automatically handles timezone conversion from UTC to local time
      const dateKey = getLocalDayKey(workout.start_time) // YYYY-MM-DD in user's local timezone
      const workoutDate = new Date(workout.start_time) // Keep original timestamp for sorting/display

      const totalSets =
        workout.exercises?.reduce((sum, exercise) => sum + (exercise.sets?.length || 0), 0) || 0
      const muscleGroupsSet = new Set(
        workout.exercises?.flatMap((exercise) =>
          this.getMuscleGroupsFromExercise(exercise, false)
        ) || []
      )
      const muscleGroups = Array.from(muscleGroupsSet)

      if (calendarMap.has(dateKey)) {
        // Merge with existing day's workouts
        const existing = calendarMap.get(dateKey)!
        existing.routineNames.push(workout.title || 'Workout')
        existing.routineColors.push(this.getWorkoutColor(workout.title || ''))
        existing.workoutIds.push(workout.id)
        existing.sets += totalSets
        // Merge muscle groups without duplicates
        const allMuscleGroups = new Set([...existing.muscleGroups, ...muscleGroups])
        existing.muscleGroups = Array.from(allMuscleGroups)
      } else {
        // Create new day entry
        calendarMap.set(dateKey, {
          date: workoutDate,
          routineNames: [workout.title || 'Workout'],
          routineColors: [this.getWorkoutColor(workout.title || '')],
          workoutIds: [workout.id],
          sets: totalSets,
          muscleGroups
        })
      }
    })

    return Array.from(calendarMap.values())
  }

  /**
   * Transform workouts into exercise chart data
   * Creates one data point per exercise per day with best performance
   */
  static processWorkoutsToExercises(workouts: Workout[]): CategorizedOneRepMax[] {
    // Ensure all workouts are processed
    const processedWorkouts = this.ensureWorkoutsProcessed(workouts)

    // Create exercise data for charts - ONE data point per exercise per day
    const exerciseMap = new Map<string, CategorizedOneRepMax>()

    processedWorkouts.forEach((workout) => {
      workout.exercises?.forEach((exercise) => {
        const muscleGroups = this.getMuscleGroupsFromExercise(exercise, false)

        // Find the best performance for this exercise on this day
        let bestOneRepMax = 0
        let bestWeight = 0
        let bestReps = 0
        let maxReps = 0
        const isBodyweight =
          exercise.isBodyweight ||
          (exercise.sets?.every((s) => s.weight_kg === null) && exercise.sets?.some((s) => s.reps))

        exercise.sets?.forEach((set) => {
          // Include both weighted and bodyweight exercises
          // Hevy API returns weight_kg: null for bodyweight exercises
          if ((set.weight_kg !== null && set.reps) || (set.weight_kg === null && set.reps)) {
            const weight = set.weight_kg || 0 // Use 0 for bodyweight exercises (null becomes 0)

            if (isBodyweight) {
              // For bodyweight exercises, track max reps instead of 1RM
              if (set.reps > maxReps) {
                maxReps = set.reps
                bestReps = set.reps
                bestWeight = weight
                bestOneRepMax = set.reps // Use reps as the primary metric
              }
            } else {
              // For weighted exercises, use 1RM calculation
              const oneRepMax = calculate1RM(weight, set.reps)
              if (oneRepMax > bestOneRepMax) {
                bestOneRepMax = oneRepMax
                bestWeight = weight
                bestReps = set.reps
              }
            }
          }
        })

        // Add if we found valid data (include bodyweight exercises with reps)
        if (bestOneRepMax > 0 || bestReps > 0) {
          muscleGroups.forEach((muscleGroup) => {
            const key = `${exercise.title}-${muscleGroup}`

            if (!exerciseMap.has(key)) {
              exerciseMap.set(key, {
                name: exercise.title,
                primaryMover: muscleGroup,
                chartData: [],
                currentMax: 0
              })
            }

            const exerciseData = exerciseMap.get(key)!

            // Add ONLY the best performance from this day
            exerciseData.chartData.push({
              date: workout.start_time,
              oneRepMax: bestOneRepMax,
              weight: bestWeight,
              reps: bestReps,
              maxReps: isBodyweight ? maxReps : undefined,
              isBodyweight: isBodyweight
            })

            // Update current max
            if (bestOneRepMax > exerciseData.currentMax) {
              exerciseData.currentMax = bestOneRepMax
            }
          })
        }
      })
    })

    return Array.from(exerciseMap.values())
      .filter((exercise) => exercise.chartData.length > 0) // Only include exercises with data
      .map((exercise) => ({
        ...exercise,
        // Sort chart data by date
        chartData: exercise.chartData.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        )
      }))
  }

  /**
   * Calculate weekly stats from workouts using proper muscle group analysis
   * Preserves complex primary/secondary mover logic for accurate volume tracking
   */
  static processWorkoutsToStats(workouts: Workout[]): WeeklyStats {
    // Ensure all workouts are processed
    const processedWorkouts = this.ensureWorkoutsProcessed(workouts)

    // Get workouts from the last 7 days using local date keys for accurate day boundaries
    const today = new Date()
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(today.getDate() - 7)
    const fourteenDaysAgo = new Date()
    fourteenDaysAgo.setDate(today.getDate() - 14)

    const todayKey = today.toLocaleDateString('sv-SE')
    const sevenDaysAgoKey = sevenDaysAgo.toLocaleDateString('sv-SE')
    const fourteenDaysAgoKey = fourteenDaysAgo.toLocaleDateString('sv-SE')

    const thisWeekWorkouts = processedWorkouts.filter((w) => {
      const workoutDateKey = getLocalDayKey(w.start_time)
      return workoutDateKey >= sevenDaysAgoKey && workoutDateKey <= todayKey
    })
    const lastWeekWorkouts = processedWorkouts.filter((w) => {
      const workoutDateKey = getLocalDayKey(w.start_time)
      return workoutDateKey >= fourteenDaysAgoKey && workoutDateKey < sevenDaysAgoKey
    })

    // Calculate detailed stats for this week
    const thisWeekStats = this.calculateDetailedMuscleGroupStats(thisWeekWorkouts)
    const lastWeekStats = this.calculateDetailedMuscleGroupStats(lastWeekWorkouts)

    return {
      sessionCount: thisWeekWorkouts.length,
      totalPhysicalSets: thisWeekStats.totalPhysicalSets,
      chestSets: thisWeekStats.chestSets,
      latsSets: thisWeekStats.latsSets,
      upperBackSets: thisWeekStats.upperBackSets,
      rearDeltsSets: thisWeekStats.rearDeltsSets,
      lateralDeltsSets: thisWeekStats.lateralDeltsSets,
      frontDeltsSets: thisWeekStats.frontDeltsSets,
      bicepsSets: thisWeekStats.bicepsSets,
      tricepsSets: thisWeekStats.tricepsSets,
      quadsSets: thisWeekStats.quadsSets,
      hamstringsSets: thisWeekStats.hamstringsSets,
      glutesSets: thisWeekStats.glutesSets,
      adductorsSets: thisWeekStats.adductorsSets,
      calvesSets: thisWeekStats.calvesSets,
      absSets: thisWeekStats.absSets,
      forearmsSets: thisWeekStats.forearmsSets,
      neckSets: thisWeekStats.neckSets,
      weightData: [],
      exerciseBreakdown: thisWeekStats.exerciseBreakdown,
      previousWeek: {
        totalPhysicalSets: lastWeekStats.totalPhysicalSets,
        chestSets: lastWeekStats.chestSets,
        latsSets: lastWeekStats.latsSets,
        upperBackSets: lastWeekStats.upperBackSets,
        rearDeltsSets: lastWeekStats.rearDeltsSets,
        lateralDeltsSets: lastWeekStats.lateralDeltsSets,
        frontDeltsSets: lastWeekStats.frontDeltsSets,
        bicepsSets: lastWeekStats.bicepsSets,
        tricepsSets: lastWeekStats.tricepsSets,
        quadsSets: lastWeekStats.quadsSets,
        hamstringsSets: lastWeekStats.hamstringsSets,
        glutesSets: lastWeekStats.glutesSets,
        adductorsSets: lastWeekStats.adductorsSets,
        calvesSets: lastWeekStats.calvesSets,
        absSets: lastWeekStats.absSets,
        forearmsSets: lastWeekStats.forearmsSets,
        neckSets: lastWeekStats.neckSets,
        exerciseBreakdown: lastWeekStats.exerciseBreakdown
      }
    }
  }

  /**
   * Calculate detailed muscle group stats with exercise breakdown
   * CRITICAL: Preserves exact business logic for primary/secondary mover volume counting
   */
  private static calculateDetailedMuscleGroupStats(workouts: Workout[]): {
    totalPhysicalSets: number
    chestSets: number
    latsSets: number
    upperBackSets: number
    rearDeltsSets: number
    lateralDeltsSets: number
    frontDeltsSets: number
    bicepsSets: number
    tricepsSets: number
    quadsSets: number
    hamstringsSets: number
    glutesSets: number
    adductorsSets: number
    calvesSets: number
    absSets: number
    forearmsSets: number
    neckSets: number
    exerciseBreakdown: Record<string, Array<{ name: string; sets: number; isPrimary?: boolean }>>
  } {
    // Initialize stats
    const stats = {
      totalPhysicalSets: 0,
      chestSets: 0,
      latsSets: 0,
      upperBackSets: 0,
      rearDeltsSets: 0,
      lateralDeltsSets: 0,
      frontDeltsSets: 0,
      bicepsSets: 0,
      tricepsSets: 0,
      quadsSets: 0,
      hamstringsSets: 0,
      glutesSets: 0,
      adductorsSets: 0,
      calvesSets: 0,
      absSets: 0,
      forearmsSets: 0,
      neckSets: 0,
      exerciseBreakdown: {} as Record<
        string,
        Array<{ name: string; sets: number; isPrimary?: boolean }>
      >
    }

    // Initialize exercise breakdown for all muscle groups
    const muscleGroups: MuscleGroup[] = [
      'chest',
      'lats',
      'upperBack',
      'rearDelts',
      'lateralDelts',
      'frontDelts',
      'biceps',
      'triceps',
      'quads',
      'hamstrings',
      'glutes',
      'adductors',
      'calves',
      'abs',
      'forearms',
      'neck'
    ]

    muscleGroups.forEach((mg) => {
      stats.exerciseBreakdown[mg] = []
    })

    workouts.forEach((workout) => {
      workout.exercises?.forEach((exercise) => {
        const setsCount = exercise.sets?.length || 0
        stats.totalPhysicalSets += setsCount

        // Handle primary mover - gets full volume count
        const primaryMover = exercise.primaryMover
        if (primaryMover) {
          // Map muscle group to stats property
          const statsKey = `${primaryMover}Sets` as keyof typeof stats
          if (typeof stats[statsKey] === 'number') {
            ;(stats[statsKey] as number) += setsCount
          }

          // Add to exercise breakdown as primary
          if (!stats.exerciseBreakdown[primaryMover]) {
            stats.exerciseBreakdown[primaryMover] = []
          }

          const existingPrimary = stats.exerciseBreakdown[primaryMover].find(
            (ex) => ex.name === exercise.title && ex.isPrimary !== false
          )

          if (existingPrimary) {
            existingPrimary.sets += setsCount
          } else {
            stats.exerciseBreakdown[primaryMover].push({
              name: exercise.title,
              sets: setsCount,
              isPrimary: true
            })
          }
        }

        // Handle secondary movers - only add to exercise breakdown, NOT volume counts
        const secondaryMovers = exercise.secondaryMovers || []
        secondaryMovers.forEach((muscleGroup) => {
          // DO NOT add to volume counts - only add to exercise breakdown for tooltips
          // Volume counts will be handled by recalculateStatsWithSecondaryMovers if needed

          // Add to exercise breakdown as secondary
          if (!stats.exerciseBreakdown[muscleGroup]) {
            stats.exerciseBreakdown[muscleGroup] = []
          }

          const existingSecondary = stats.exerciseBreakdown[muscleGroup].find(
            (ex) => ex.name === exercise.title && ex.isPrimary === false
          )

          if (existingSecondary) {
            existingSecondary.sets += setsCount
          } else {
            stats.exerciseBreakdown[muscleGroup].push({
              name: exercise.title,
              sets: setsCount,
              isPrimary: false
            })
          }
        })
      })
    })

    return stats
  }

  /**
   * Unified processing pipeline for any workout data source
   * Returns all transformed data formats needed by the UI
   */
  static processWorkoutData(workouts: Workout[]): {
    calendarData: ExtendedCalendarDay[]
    allExercises: CategorizedOneRepMax[]
    oneRepMaxes: CategorizedOneRepMax[]
    weeklyStats: WeeklyStats
    workouts: Workout[]
  } {
    // Process workouts once to ensure consistency
    const processedWorkouts = this.ensureWorkoutsProcessed(workouts)

    // Transform into all required formats
    const calendarData = this.processWorkoutsToCalendar(processedWorkouts)
    const allExercises = this.processWorkoutsToExercises(processedWorkouts)
    const weeklyStats = this.processWorkoutsToStats(processedWorkouts)

    return {
      calendarData,
      allExercises,
      oneRepMaxes: allExercises, // Simplified - same as allExercises
      weeklyStats,
      workouts: processedWorkouts
    }
  }
}